// React
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  Menu,
  MenuItem,
  Modal,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";

// Custom UI components
import borders from "assets/theme/base/borders";
import colors from "assets/theme/base/colors";
import typography from "assets/theme/base/typography";
import Loading from "components/Carteles/Loading";
import SinDatos from "components/Carteles/SinDatos";
import SinResultados from "components/Carteles/SinResultados";
import SoftBox from "components/SoftBox";
import SoftButton from "components/SoftButton";
import SoftInput from "components/SoftInput";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import { ConfirmarContext } from "context/ConfirmarContext";
import { getUproColor } from "utils/colors";
import { formatMoneyPunto } from "utils/formatters";
import ModalAddPlantilla from "../../ModalAddPlantilla";
import ModalProducto from "../../ModalProducto";
import TableItem from "../TableItem";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "80%", xxl: "70%" },
  overflow: "auto",
  p: 3,
};

const columnsBase = [
  { name: "type", desc: " ", align: "left", width: "1%", noOrder: true },
  { name: "id", desc: "ID", width: "1%", align: "left" },
  { name: "nombre", desc: "Nombre", align: "left" },
  { name: "categoria", desc: "Rubro", align: "left" },
  { name: "cantidad", desc: "cantidad", align: "center", width: "150px" },
  { name: "precio", desc: "precio", align: "center", width: "150px" },
  { name: "eliminar", desc: " ", align: "left", width: "1%", noOrder: true },
];

const columnsSelect = [
  { name: "type", desc: " ", align: "left", width: "1%", noOrder: true },
  { name: "id", desc: "ID", align: "left" },
  { name: "nombre", desc: "Nombre", align: "left" },
  { name: "categoria", desc: "Rubro", align: "left" },
  { name: "cantidad", desc: "cantidad", align: "center" },
  { name: "precio", desc: "precio", align: "center" },
];

const INTEGER_EPSILON = 1e-6;

const toSafeInt = (value) => {
  const numericValue = Number(value) || 0;
  const roundedValue = Math.round(numericValue);
  if (Math.abs(numericValue - roundedValue) > INTEGER_EPSILON) {
    return null;
  }
  return roundedValue;
};

export default function ModalNuevoRemito({
  open,
  handleClose,
  selectedRemito,
  idSucursal,
  refetchProductos,
}) {
  const [openSelectionMenu, setOpenSelectionMenu] = useState(false);
  const [productoToAdd, setProductoToAdd] = useState();
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [productosRemito, setProductosRemito] = useState([]);
  const [openModalProducto, setOpenModalProducto] = useState(false);
  const [selectedItem, setSelectedItem] = useState();
  const [openAddMenu, setOpenAddMenu] = useState(false);
  const { light } = colors;
  const { size, fontWeightBold } = typography;
  const { borderWidth } = borders;
  const { handleSnackbar } = useContext(MessageManager);
  const [getProveedores, { data: proveedores }] = useLazyQuery(
    gql`
      query getProveedores($order: Order) {
        proveedores(order: $order) {
          id
          nombre
          apellido
          razonSocial
        }
      }
    `
  );
  const [remito, setRemito] = useState("");

  const [getProductos, { data: productos, loading }] = useLazyQuery(
    gql`
      query getProductos($page: Int, $order: Order, $filter: JSON) {
        paginationInfo {
          pages
          total
        }
        materiasPrimas(pagination: { page: $page, limit: 50 }, order: $order, filter: $filter) {
          id
          codigo
          tipo
          categoria
          nombre
          disabled
          precio
          tipoUnidad {
            id
            nombre
            abreviatura
            multiplicador
          }
        }
      }
    `,
    {
      fetchPolicy: "network-only",
    }
  );
  const [saveRemito, { loading: loadingSave }] = useMutation(
    gql`
      mutation saveMovimiento($input: MovimientoInput!) {
        saveMovimiento(input: $input) {
          id
        }
      }
    `
  );

  const handleChanges = (event) => {
    setRemito({ ...remito, [event.target.name]: event.target.value });
  };

  const handleOpenAddMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setOpenAddMenu(true);
  };

  const handleCloseAddMenu = () => {
    setAnchorEl(null);
    setOpenAddMenu(false);
  };

  const renderAddMenu = (
    <Menu
      id="simple-menu"
      anchorEl={anchorEl}
      open={Boolean(openAddMenu)}
      onClose={handleCloseAddMenu}
      transformOrigin={{ horizontal: "right", vertical: "top" }}
      anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: "visible",
          filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.2))",
          mt: 1.5,
          "& .MuiAvatar-root": {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
          },
          "&::before": {
            content: '""',
            display: "block",
            position: "absolute",
            top: 0,
            right: 14,
            width: 10,
            height: 10,
            bgcolor: "background.paper",
            transform: "translateY(-50%) rotate(45deg)",
            zIndex: 0,
          },
        },
      }}
    >
      <MenuItem
        onClick={() => {
          setOpenModalProducto(true);
          setSelectedItem({ tipo: "Material" });
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <SoftBox>
            <SoftTypography variant="p">Material</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                color: getUproColor("uproYellow"),
              }}
            >
              category
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
      <MenuItem
        onClick={() => {
          setOpenModalProducto(true);
          setSelectedItem({ tipo: "Consumible" });
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <SoftBox>
            <SoftTypography variant="p">Consumible</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                color: getUproColor("uproGreen"),
              }}
            >
              recycling
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
      <MenuItem
        onClick={() => {
          setOpenModalProducto(true);
          setSelectedItem({ tipo: "Recurso" });
        }}
      >
        <SoftBox
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{
            width: "100%",
          }}
        >
          <SoftBox>
            <SoftTypography variant="p">Recurso</SoftTypography>
          </SoftBox>
          <SoftBox>
            <Icon
              fontSize="small"
              sx={{
                verticalAlign: "middle",
                color: getUproColor("uproPink"),
              }}
            >
              layers
            </Icon>
          </SoftBox>
        </SoftBox>
      </MenuItem>
    </Menu>
  );

  const handleSearch = (event) => {
    const newTimeout = setTimeout(() => {
      getProductos({
        variables: {
          page: 1,
          filter: {
            disabled: false,
            _or: [
              {
                id: { _like: `%${event.target.value}%` },
              },
              {
                nombre: { _like: `%${event.target.value}%` },
              },
              {
                categoria: { _like: `%${event.target.value}%` },
              },
            ],
          },
        },
      });
    }, 500);
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    setSearchTimeout(newTimeout);
  };

  const handleUpdateProductoRemito = (material) => {
    setProductosRemito(
      productosRemito.map((item) => {
        if (item.id === material.id) {
          return material;
        }
        return item;
      })
    );
  };

  const handleAddProduct = (product) => {
    setProductosRemito([...productosRemito, product]);
  };

  const handleRemoveProduct = (product) => {
    setProductosRemito(productosRemito.filter((item) => item.id !== product.id));
  };

  const renderColumnsBase = columnsBase.map(({ name, desc, align, width }, key) => {
    let pl;
    let pr;

    if (key === 0) {
      pl = 3;
      pr = 3;
    } else if (key === columnsBase.length - 1) {
      pl = 3;
      pr = 3;
    } else {
      pl = 1;
      pr = 1;
    }

    return (
      <SoftBox
        key={name}
        component="th"
        width={width || "auto"}
        pt={1.5}
        pb={1.25}
        pl={align === "left" ? pl : 3}
        pr={align === "right" ? pr : 3}
        textAlign={align}
        fontSize={size.xxs}
        fontWeight={fontWeightBold}
        color="secondary"
        opacity={0.7}
        borderBottom={`${borderWidth[1]} solid ${light.main}`}
      >
        {desc ? desc.toUpperCase() : name.toUpperCase()}
      </SoftBox>
    );
  });

  const renderColumnsSelect = columnsSelect.map(({ name, desc, align, width }, key) => {
    let pl;
    let pr;

    if (key === 0) {
      pl = 3;
      pr = 3;
    } else if (key === columnsSelect.length - 1) {
      pl = 1;
      pr = 1;
    } else {
      pl = 1;
      pr = 1;
    }

    return (
      <SoftBox
        key={name}
        component="th"
        width={width || "auto"}
        pt={1.5}
        pb={1.25}
        pl={align === "left" ? pl : 3}
        pr={align === "right" ? pr : 3}
        textAlign={align}
        fontSize={size.xxs}
        fontWeight={fontWeightBold}
        color="secondary"
        opacity={0.7}
        borderBottom={`${borderWidth[1]} solid ${light.main}`}
      >
        {desc ? desc.toUpperCase() : name.toUpperCase()}
      </SoftBox>
    );
  });

  const handleSave = () => {
    const movimientoDetalle = [];
    for (const item of productosRemito) {
      const cantidadBase = (Number(item.cantidad) || 0) * (Number(item?.tipoUnidad?.multiplicador) || 1);
      const cantidadInt = toSafeInt(cantidadBase);

      if (cantidadInt === null) {
        handleSnackbar(
          `La cantidad para ${item?.nombre || `MP #${item?.id}`} no es un entero valido (${cantidadBase}).`,
          "error"
        );
        return;
      }

      movimientoDetalle.push({
        idMateriaPrima: item.id,
        cantidad: cantidadInt,
        idSucursal: idSucursal,
        precio: item.precio,
      });
    }

    saveRemito({
      variables: {
        input: {
          tipo: "Remito Proveedor",
          movimientoDetalle,
          idSucursalDestino: idSucursal,
          fechaEntrega: remito.fechaEntrega,
          idProveedor: remito.idProveedor,
          fechaOrdenPago: remito.fechaOrdenPago,
          ordenPago: remito.ordenPago,
          observaciones: remito.observaciones,
        },
      },
    })
      .then(() => {
        handleSnackbar("El remito se ha guardado correctamente", "success");
        handleClose();
        refetchProductos();
      })
      .catch(() => {
        handleSnackbar("Ha ocurrido un error al guardar el remito", "error");
      });
  };

  useEffect(() => {
    setRemito();
    setProductosRemito([]);
    if (selectedRemito && open) {
      let productosCargados = selectedRemito.movimientoDetalles.map((item) => ({
        ...item.materiaPrima,
        cantidad: item.cantidad / item.materiaPrima.tipoUnidad.multiplicador,
        precio: item.precio,
      }));
      setProductosRemito(productosCargados);
    }
  }, [open]);

  useEffect(() => {
    if (productoToAdd && productos?.materiasPrimas?.length > 0) {
      if (productos.materiasPrimas.find((producto) => producto.id === productoToAdd)) {
        handleAddProduct(
          productos.materiasPrimas.find((producto) => producto.id === productoToAdd)
        );
      }
      setProductoToAdd();
    }
  }, [productos]);

  useEffect(() => {
    if (open) {
      getProveedores({ variables: { order: { field: "nombre", order: "ASC" } } });
    }
  }, [open]);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox>
            <SoftBox mb={2}>
              <Card>
                <SoftBox
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  p={3}
                  pb={0}
                >
                  <SoftTypography variant="h6">Datos del remito</SoftTypography>
                  <SoftBox>
                    <Tooltip title="Cerrar" placement="top">
                      <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                        close
                      </Icon>
                    </Tooltip>
                  </SoftBox>
                </SoftBox>
                <SoftBox p={3}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={3}>
                      <InputLabel htmlFor="provision">
                        <Typography component="label" variant="caption" fontWeight="bold">
                          Proveedor
                          {!selectedRemito && (
                            <SoftTypography variant="caption" fontWeight="bold" color="primary">
                              &nbsp;*
                            </SoftTypography>
                          )}
                        </Typography>
                      </InputLabel>
                      <SoftBox>
                        {selectedRemito ? (
                          <SoftTypography variant="h6" fontWeight="bold">
                            {selectedRemito.proveedor.razonSocial ||
                              `${selectedRemito.proveedor.nombre} ${selectedRemito.proveedor.apellido}`}
                          </SoftTypography>
                        ) : (
                          <Select
                            value={remito?.idProveedor || -1}
                            onChange={handleChanges}
                            name="idProveedor"
                            disabled={selectedRemito ? true : false}
                            fullWidth
                          >
                            <MenuItem value={-1}>Seleccione un proveedor</MenuItem>
                            {proveedores?.proveedores?.map((proveedor) => (
                              <MenuItem key={proveedor.id} value={proveedor.id}>
                                {proveedor.razonSocial ||
                                  `${proveedor.nombre} ${proveedor.apellido}`}
                              </MenuItem>
                            ))}
                          </Select>
                        )}
                      </SoftBox>
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <InputLabel htmlFor="ordenPago">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Número de orden de pago
                          {!selectedRemito && (
                            <SoftTypography variant="caption" fontWeight="bold" color="primary">
                              &nbsp;*
                            </SoftTypography>
                          )}
                        </SoftTypography>
                      </InputLabel>
                      {selectedRemito ? (
                        <SoftTypography variant="h6" fontWeight="bold">
                          {selectedRemito.ordenPago}
                        </SoftTypography>
                      ) : (
                        <SoftInput
                          label="ordenPago"
                          placeholder="Ingrese un número de orden de pago"
                          type="text"
                          name="ordenPago"
                          value={remito?.ordenPago || ""}
                          onChange={handleChanges}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <InputLabel htmlFor="fechaEntrega">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Fecha de Entrega
                          {!selectedRemito && (
                            <SoftTypography variant="caption" fontWeight="bold" color="primary">
                              &nbsp;*
                            </SoftTypography>
                          )}
                        </SoftTypography>
                      </InputLabel>
                      {selectedRemito ? (
                        <SoftTypography variant="h6" fontWeight="bold">
                          {selectedRemito.fechaEntrega}
                        </SoftTypography>
                      ) : (
                        <SoftInput
                          label="fechaEntrega"
                          type="date"
                          name="fechaEntrega"
                          value={remito?.fechaEntrega || ""}
                          onChange={handleChanges}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12} sm={3}>
                      <InputLabel htmlFor="fechaEntrega">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Fecha de Orden de Pago
                          {!selectedRemito && (
                            <SoftTypography variant="caption" fontWeight="bold" color="primary">
                              &nbsp;*
                            </SoftTypography>
                          )}
                        </SoftTypography>
                      </InputLabel>
                      {selectedRemito ? (
                        <SoftTypography variant="h6" fontWeight="bold">
                          {selectedRemito.fechaOrdenPago}
                        </SoftTypography>
                      ) : (
                        <SoftInput
                          label="fechaOrdenPago"
                          type="date"
                          name="fechaOrdenPago"
                          value={remito?.fechaOrdenPago || ""}
                          onChange={handleChanges}
                        />
                      )}
                    </Grid>
                    <Grid item xs={12}>
                      <InputLabel htmlFor="observaciones">
                        <SoftTypography component="label" variant="caption" fontWeight="bold">
                          Observaciones
                        </SoftTypography>
                      </InputLabel>
                      {selectedRemito ? (
                        <SoftTypography variant="h6" fontWeight="bold">
                          {selectedRemito.observaciones || "Sin observaciones"}
                        </SoftTypography>
                      ) : (
                        <SoftInput
                          label="observaciones"
                          placeholder="Ingrese un comentario"
                          type="text"
                          name="observaciones"
                          value={remito?.observaciones || ""}
                          onChange={handleChanges}
                          multiline
                          rows={1}
                        />
                      )}
                    </Grid>
                  </Grid>
                </SoftBox>
              </Card>
            </SoftBox>
            <SoftBox>
              <Card>
                <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={3}>
                  <SoftBox>
                    <SoftTypography variant="h6">Productos</SoftTypography>
                  </SoftBox>
                  {!selectedRemito ? (
                    <SoftBox display="flex" justifyContent="end" alignItems="flex-end">
                      <SoftBox
                        mr={2}
                        sx={{
                          width: "400px",
                        }}
                      >
                        <SoftInput
                          placeholder="Busca aquí materiales..."
                          icon={{ component: "search", direction: "left" }}
                          onChange={handleSearch}
                          onKeyPress={() => {
                            setOpenSelectionMenu(true);
                          }}
                          onClick={() => {
                            getProductos({
                              variables: {
                                page: 1,
                                filter: {
                                  disabled: false,
                                },
                              },
                            });
                            setOpenSelectionMenu(true);
                          }}
                          onBlur={(e) => {
                            let parent = e.relatedTarget?.closest("#custom-autocomplete");
                            e.preventDefault();
                            if (!parent) {
                              setOpenSelectionMenu(false);
                            } else {
                              e.target.focus();
                            }
                          }}
                        />
                        {openSelectionMenu && (
                          <SoftBox id="custom-autocomplete" sx={{ position: "fixed", zIndex: 99 }}>
                            <a
                              href=""
                              onClick={(e) => {
                                e.preventDefault();
                              }}
                            >
                              <Card>
                                <SoftBox
                                  p={
                                    !loading &&
                                    productos?.materiasPrimas?.length > 0 &&
                                    productos?.materiasPrimas.filter(
                                      (materiaPrima) =>
                                        !productosRemito.find((item) => item.id === materiaPrima.id)
                                    ).length > 0
                                      ? 3
                                      : 0
                                  }
                                  sx={{
                                    width: "400px",
                                    maxHeight: "240px",
                                    overflowY: "auto",
                                    overflowX: "hidden",
                                  }}
                                >
                                  {openSelectionMenu && loading ? (
                                    <Loading />
                                  ) : productos?.materiasPrimas?.length > 0 &&
                                    productos?.materiasPrimas.filter(
                                      (materiaPrima) =>
                                        !productosRemito.find((item) => item.id === materiaPrima.id)
                                    ).length > 0 &&
                                    openSelectionMenu ? (
                                    productos?.materiasPrimas
                                      .filter(
                                        (producto) =>
                                          !productosRemito.find((item) => item.id === producto.id)
                                      )
                                      .map((producto) => (
                                        <MenuItem
                                          key={producto.id}
                                          onClick={() => {
                                            handleAddProduct(producto);
                                            setOpenSelectionMenu(false);
                                          }}
                                          sx={{ whiteSpace: "normal" }}
                                        >
                                          <SoftBox mr={2}>
                                            <Icon
                                              fontSize="small"
                                              sx={{
                                                color:
                                                  producto.tipo === "Material"
                                                    ? getUproColor("uproYellow")
                                                    : producto.tipo === "Consumible"
                                                    ? getUproColor("uproGreen")
                                                    : getUproColor("uproPink"),
                                              }}
                                            >
                                              {producto.tipo === "Material"
                                                ? "category"
                                                : producto.tipo === "Consumible"
                                                ? "recycling"
                                                : "layers"}
                                            </Icon>
                                          </SoftBox>
                                          <SoftBox>
                                            &nbsp;{`${producto.id} - ${producto.nombre}`}
                                          </SoftBox>
                                        </MenuItem>
                                      ))
                                  ) : (
                                    <SinResultados />
                                  )}
                                </SoftBox>
                              </Card>
                            </a>
                          </SoftBox>
                        )}
                      </SoftBox>
                      <SoftBox>
                        <Tooltip title={"Agregar producto"} placement="top">
                          <SoftButton
                            color="uproGreen"
                            circular
                            iconOnly
                            onClick={handleOpenAddMenu}
                          >
                            <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                          </SoftButton>
                        </Tooltip>
                      </SoftBox>
                    </SoftBox>
                  ) : (
                    <SoftButton color="uproGreen" circular>
                      {`Total $${formatMoneyPunto(
                        productosRemito.reduce((acc, item) => {
                          return acc + item.cantidad * item.precio;
                        }, 0)
                      )}`}
                    </SoftButton>
                  )}
                </SoftBox>
                <SoftBox
                  sx={{
                    "& .MuiTableRow-root:not(:last-child)": {
                      "& td": {
                        borderBottom: ({ borders: { borderWidth, borderColor } }) =>
                          `${borderWidth[1]} solid ${borderColor}`,
                      },
                    },
                  }}
                >
                  {productosRemito.length > 0 ? (
                    <TableContainer>
                      <Table>
                        <SoftBox component="thead">
                          {selectedRemito ? (
                            <TableRow>{renderColumnsSelect}</TableRow>
                          ) : (
                            <TableRow>{renderColumnsBase}</TableRow>
                          )}
                        </SoftBox>
                        <TableBody>
                          {productosRemito.map((material) => {
                            return (
                              <TableItem
                                key={material.id}
                                material={material}
                                handleUpdateMaterial={handleUpdateProductoRemito}
                                handleRemoveMaterial={() => handleRemoveProduct(material)}
                                disabled={selectedRemito ? true : false}
                              />
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  ) : (
                    <SinDatos />
                  )}
                </SoftBox>
              </Card>
              {!selectedRemito && (
                <Card sx={{ marginTop: 2 }}>
                  <SoftBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
                    <SoftBox>
                      <SoftBox>
                        <SoftButton color="uproGreen" circular>
                          {`Total $${formatMoneyPunto(
                            productosRemito.reduce((acc, item) => {
                              return acc + item.cantidad * item.precio;
                            }, 0)
                          )}`}
                        </SoftButton>
                      </SoftBox>
                    </SoftBox>
                    <SoftBox display="flex" justifyContent="end" alignItems="center">
                      <SoftBox mr={2}>
                        <SoftButton color="primary" circular onClick={handleClose}>
                          <Icon sx={{ fontWeight: "light" }}>cancel</Icon>
                          &nbsp;Cancelar
                        </SoftButton>
                      </SoftBox>
                      <SoftBox>
                        <SoftButton
                          color="uproGreen"
                          circular
                          disabled={
                            loadingSave ||
                            remito?.estado === "Finalizado" ||
                            productosRemito.length === 0 ||
                            productosRemito.some(
                              (item) =>
                                !item.cantidad ||
                                item.cantidad == 0 ||
                                item?.cantidad?.toString().includes(".")
                            ) ||
                            !remito?.fechaEntrega ||
                            !remito?.idProveedor ||
                            !remito?.ordenPago ||
                            !remito?.fechaOrdenPago
                          }
                          onClick={handleSave}
                        >
                          {loadingSave ? (
                            <CircularProgress size={15} color="white" />
                          ) : (
                            <Icon sx={{ fontWeight: "light" }}>save</Icon>
                          )}
                          &nbsp;Guardar
                        </SoftButton>
                      </SoftBox>
                    </SoftBox>
                  </SoftBox>
                </Card>
              )}
            </SoftBox>
            {renderAddMenu}
            <ModalProducto
              id={selectedItem?.id || null}
              tipo={selectedItem?.tipo || null}
              open={openModalProducto}
              handleClose={() => {
                setOpenModalProducto(false);
                handleCloseAddMenu();
              }}
              refetch={() => {}}
              onCreate={(newId) => {
                getProductos({
                  variables: {
                    page: 1,
                    filter: {
                      disabled: false,
                      id: newId,
                    },
                  },
                });
                setProductoToAdd(newId);
              }}
            />
          </SoftBox>
        </Card>
      </Fade>
    </Modal>
  );
}

ModalNuevoRemito.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  selectedRemito: PropTypes.object,
  idSucursal: PropTypes.string || PropTypes.number,
  refetchProductos: PropTypes.func,
};
